import { useMemo, useRef, useCallback } from 'react';
import { useFlightDataContext, usePlaybackContext, FlightPosition } from 'src/@realtime/contexts';
import {
  point,
  lineString,
  featureCollection,
  Feature,
  Point,
  LineString,
  nearestPointOnLine,
} from '@turf/turf';
import { SpriteKeys } from 'src/@realtime/utils/sprites';
import { PLAYBACK_TICK_INTERVAL_MS, SHADOW_ICON_OFFSETS } from 'src/@realtime/constants';
import { findPosition } from 'src/@realtime/utils/flight';
import { TailLineStringProperties } from 'src/@realtime/types';
import {} from '@turf/turf';

interface FlightProperties {
  trackId: string;
  iconKey: SpriteKeys;
  shadowOffset: [number, number];
  heading: number;
  operationType: string;
  operationIconKey: string;
  altitude: number;
}

// Extract helicopter blade animation logic
const getHelicopterIcon = (
  aircraftCategory: string,
  heliBladeToggleRef: React.MutableRefObject<boolean>,
  lastBladeToggleTimeRef: React.MutableRefObject<number>
): SpriteKeys => {
  if (aircraftCategory.toLowerCase() !== 'helicopter') {
    return aircraftCategory.toLowerCase() as SpriteKeys;
  }

  const now = Date.now();
  if (now - lastBladeToggleTimeRef.current >= PLAYBACK_TICK_INTERVAL_MS) {
    heliBladeToggleRef.current = !heliBladeToggleRef.current;
    lastBladeToggleTimeRef.current = now;
  }

  return heliBladeToggleRef.current ? SpriteKeys.helicopter1 : SpriteKeys.helicopter2;
};

export const useBuildFlightData = () => {
  const {
    state: { currentTimestamp },
  } = usePlaybackContext();
  const {
    state: { positions, flightInfo, selectedTracks },
  } = useFlightDataContext();

  const lastUpdateRef = useRef<number>(0); // Track the last update time
  const updateInterval = 100; // Throttle updates to every 100ms

  const headingCache = useRef(new Map<string, number>());
  const lastBladeToggleTimeRef = useRef(Date.now());
  const heliBladeToggleRef = useRef(false);

  const previousFlightFeaturesRef = useRef<Feature<Point, FlightProperties>[]>([]);
  const previousTailFeaturesRef = useRef<Feature<LineString, TailLineStringProperties>[]>([]);

  return useMemo(() => {
    const now = Date.now();
    if (now - lastUpdateRef.current < updateInterval) {
      // Skip update if within throttling interval
      return {
        flightData: featureCollection(previousFlightFeaturesRef.current),
        tailData: featureCollection(previousTailFeaturesRef.current),
      };
    }

    const flightFeatures: Feature<Point, FlightProperties>[] = [];
    const tailFeatures: Feature<LineString, TailLineStringProperties>[] = [];

    for (const [trackId, flightPositions] of Object.entries(positions)) {
      if (flightPositions.length < 2) {
        continue;
      }

      const isSelected = selectedTracks.includes(trackId);
      const [pos] = findPosition(flightPositions, currentTimestamp);
      if (!pos) {
        continue;
      }

      const tail = isSelected
        ? flightPositions
        : flightPositions.filter(position => position.time < currentTimestamp).slice(-60);

      const { longitude, latitude, altitude, heading } = pos;
      const flightData = flightInfo[trackId];
      if (!flightData) {
        continue;
      }

      const roundedHeading = Math.round(heading / 15) * 15;
      headingCache.current.set(trackId, roundedHeading);

      const aircraftCategoryIcon = getHelicopterIcon(
        flightData.aircraftCategory,
        heliBladeToggleRef,
        lastBladeToggleTimeRef
      );

      flightFeatures.push(
        point<FlightProperties>(
          [longitude, latitude, altitude],
          {
            trackId,
            iconKey: aircraftCategoryIcon,
            shadowOffset: SHADOW_ICON_OFFSETS[roundedHeading] || [0, 0],
            heading,
            operationType: flightData.operationType,
            operationIconKey: flightData.operationType.toLowerCase(),
            altitude,
          },
          { id: trackId }
        )
      );

      if (tail.length > 2) {
        tailFeatures.push(
          lineString(
            tail.map(position => [position.longitude, position.latitude, position.altitude]),
            {
              trackId,
              isSelected,
              altitudes: JSON.stringify(tail.map(position => position.altitude)),
              ...flightData,
            },
            { id: trackId }
          )
        );
      }
    }

    previousFlightFeaturesRef.current = flightFeatures;
    previousTailFeaturesRef.current = tailFeatures;
    lastUpdateRef.current = now;

    return {
      flightData: featureCollection(flightFeatures),
      tailData: featureCollection(tailFeatures),
    };
  }, [currentTimestamp, positions, flightInfo, selectedTracks]);
};

export const useMatchFlightPosition = () => {
  const {
    state: { positions },
  } = useFlightDataContext();

  return useCallback(
    (
      trackId: string,
      event: mapboxgl.MapMouseEvent,
      lineFeature: GeoJSON.Feature<GeoJSON.LineString, TailLineStringProperties>
    ) => {
      const trackPositions: FlightPosition[] = positions[trackId];
      if (!trackPositions) {
        return;
      }

      // Convert the mouse event point to a Turf point.
      const { lngLat } = event;
      const turfPoint = point([lngLat.lng, lngLat.lat]);

      // Find the nearest point on the given line feature.
      const nearestPointFeature = nearestPointOnLine(lineFeature, turfPoint);
      const [nearestLng, nearestLat] = nearestPointFeature.geometry.coordinates;

      // Search through available positions until the closest one to the nearestPoint is found.
      let closestFlightPosition: FlightPosition | undefined;
      let minDistance = Number.MAX_VALUE;

      for (const position of trackPositions) {
        // Calculate the Euclidean distance.
        const distance = Math.sqrt(
          Math.pow(position.longitude - nearestLng, 2) + Math.pow(position.latitude - nearestLat, 2)
        );

        if (distance < minDistance) {
          minDistance = distance;
          closestFlightPosition = position;
        }
      }

      return closestFlightPosition;
    },
    [positions]
  );
};
